import { useEffect, useState } from "react";
import { Pin, FileText } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";

const catColor = {
  Admission: "text-gold",
  Academic: "text-forest-500",
  Examination: "text-destructive",
  Event: "text-gold",
  General: "text-muted",
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Notices() {
  const { settings } = useSite();
  const p = settings.pages.notice;
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listNotices().then((r) => setNotices(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="notice-page">
      <PageHero label={p.label} title={p.title} subtitle={p.subtitle} />
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-muted">Loading notices…</p>
          ) : notices.length === 0 ? (
            <p data-testid="no-notices" className="text-muted">No notices yet. Please check back soon.</p>
          ) : (
            <div className="divide-y divide-line border-y border-line">
              {notices.map((n, i) => (
                <Reveal key={n.id} delay={i * 0.04}>
                  <article data-testid="notice-item" className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 group">
                    <div className="md:col-span-3">
                      <p className="font-mono text-sm text-forest">{formatDate(n.created_at)}</p>
                      <p className={`text-xs uppercase tracking-widest mt-2 ${catColor[n.category] || "text-muted"}`}>{n.category}</p>
                    </div>
                    <div className="md:col-span-9">
                      <h3 className="font-serif text-2xl md:text-3xl text-forest flex items-start gap-3">
                        {n.pinned && <Pin size={18} className="text-gold mt-2 shrink-0" />}
                        <span className="group-hover:text-gold transition-colors">{n.title}</span>
                      </h3>
                      <p className="text-muted leading-relaxed mt-3 max-w-2xl">{n.body}</p>
                      {n.attachment && n.attachment_type === "image" && (
                        <a href={n.attachment} target="_blank" rel="noreferrer" className="block mt-5 max-w-md">
                          <img data-testid="notice-attachment" src={n.attachment} alt={n.attachment_name || "Notice poster"} className="w-full rounded-sm border border-line hover:opacity-90 transition-opacity" />
                        </a>
                      )}
                      {n.attachment && n.attachment_type === "pdf" && (
                        <a data-testid="notice-attachment" href={n.attachment} target="_blank" rel="noreferrer" download={n.attachment_name || "notice.pdf"} className="inline-flex items-center gap-2 mt-5 border border-forest/30 text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
                          <FileText size={15} /> {n.attachment_name || "View attachment (PDF)"}
                        </a>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
